import formatters from 'lib/formatters'
import { useFeeData } from './useFeeData'
import { encodedData } from '../../../../lib/encrypt'

export const getAccountTypeFromNumber = (accountNumber) => {
  if (!accountNumber) return null

  const firstChar = accountNumber.charAt(0).toUpperCase()

  switch (firstChar) {
    case 'G':
      return 'GPT_Old'
    case 'F':
      return 'GPT_New'
    case 'N':
    case 'D':
      return 'GTA'
    case 'T':
      return 'GSNT_Old'
    case 'J':
      return 'GSNT_New'
    case 'C':
      return 'GPT_Checking'
    default:
      return null
  }
}

export const getBankAccountType = (bankAccountValue) => {
  if (!bankAccountValue) return null

  const parts = bankAccountValue.split('_')
  if (parts.length >= 2) {
    return `${parts[0]}_${parts[1]}`
  }
  return parts[0]
}

export const shouldShowTransferMessage = ({ clientAccountNumber, bankAccount }) => {
  if (!clientAccountNumber || !bankAccount) {
    return false
  }

  const clientAccountType = getAccountTypeFromNumber(clientAccountNumber)
  const bankAccountType = getBankAccountType(bankAccount)

  if (!clientAccountType || !bankAccountType) {
    return false
  }

  return clientAccountType !== bankAccountType
}

export const formatFeeValue = (value, type = 'money', isLoading = false) => {
  if (isLoading) return '-,--'
  if (!value && value !== 0) return '--'

  switch (type) {
    case 'money':
      return formatters.money(value)
    case 'percentage':
      return formatters.percentage(value / 100)
    case 'text':
    default:
      return value
  }
}

export const createFeeFormatter = (isLoadingFees) => {
  return (value, type = 'money') => formatFeeValue(value, type, isLoadingFees)
}

export const isDepositOld = (depositDate) => {
  if (!depositDate) return false

  const depositDateObj = new Date(depositDate)
  const currentDate = new Date()
  const daysDifference = Math.floor((currentDate - depositDateObj) / (1000 * 60 * 60 * 24))

  return daysDifference > 15
}

export const getUserPermissions = () => {
  return {
    canReceive: true,
    canReview: true,
    canApprove: true,
  }
}

export const getQueueMappingsForStatus = (status) => {
  const mappings = {
    received: { tid: 'deposit_queue', stid: '0' },
    transferred: { tid: 'deposit_queue', stid: '1' },
    reviewed: { tid: 'deposit_queue', stid: '2' },
    reviewed_with_approved_edits: { tid: 'deposit_queue', stid: '3' },
    deposited_today: { tid: 'deposit_queue', stid: '4' },
    deposited: { tid: 'all_deposits', stid: '0' },
    approved: { tid: 'all_deposits', stid: '0' },
    cash: { tid: 'cash', stid: '0' },
    deleted: { tid: 'deleted', stid: '0' },
    cancelled: { tid: 'deleted', stid: '0' },
    denied: { tid: 'deleted', stid: '0' },
    failed: { tid: 'deleted', stid: '0' },
  }

  return mappings[status] || mappings.received
}

export const getQueueUrlForStatus = (status, fromQueue = null) => {
  if (status === 'reviewed' && fromQueue === 'review') {
    const reviewMapping = getQueueMappingsForStatus('reviewed')
    return `/deposits?tid=${encodedData(reviewMapping.tid)}&stid=${encodedData(reviewMapping.stid)}`
  }

  if (status === 'reviewed_with_approved_edits') {
    const mapping = getQueueMappingsForStatus('reviewed_with_approved_edits')
    return `/deposits?tid=${encodedData(mapping.tid)}&stid=${encodedData(mapping.stid)}`
  }

  let targetStatus = status

  if (['deposited', 'approved'].includes(status)) {
    targetStatus = 'deposited'
  } else if (['deleted', 'cancelled', 'denied', 'failed'].includes(status)) {
    targetStatus = 'deleted'
  }

  const mapping = getQueueMappingsForStatus(targetStatus)
  return `/deposits?tid=${encodedData(mapping.tid)}&stid=${encodedData(mapping.stid)}`
}

export const legibleDepositType = (depositType) => {
  switch (depositType) {
    case 'wire':
      return 'WIRE'
    case 'check':
      return 'CHECK'
    case 'direct_deposit':
      return 'DIRECT DEPOSIT'

    default:
      return depositType ? depositType.toUpperCase() : 'UNKNOWN'
  }
}

export const isDepositTooFarInFuture = (dateString) => {
  if (!dateString) return false

  const depositDate = new Date(dateString)
  const today = new Date()
  const twoWeeksFromToday = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000)

  return depositDate > twoWeeksFromToday
}

export { useFeeData }
